// The Nest: eggs, hatching, naming, feeding, and choosing who follows you.

import { h, clear } from '@kit/dom';
import { getSave, update, subscribe } from '../state';
import { PET_TYPES, FEED_COST, STAGE2_FED, STAGE3_FED, hatch, feed, drawPet, drawEgg } from '../pets';
import { topBar, toast } from '../ui';
import { play as sound } from '@kit/audio';

export function petsScreen(root: HTMLElement) {
  const bar = topBar('The Nest', { back: 'house' });
  const body = h('div.stack', { style: { padding: '0 16px 24px' } });
  root.append(bar, body);

  function canvasOf(draw: (c: CanvasRenderingContext2D, w: number, hh: number) => void, w = 96, hh = 96): HTMLCanvasElement {
    const cv = document.createElement('canvas');
    const dpr = window.devicePixelRatio || 1;
    cv.width = w * dpr; cv.height = hh * dpr; cv.style.width = w + 'px'; cv.style.height = hh + 'px';
    const c = cv.getContext('2d')!; c.setTransform(dpr, 0, 0, dpr, 0, 0);
    draw(c, w, hh);
    return cv;
  }

  function render() {
    clear(body);
    const s = getSave();
    if (!s.pets.eggs.length && !s.pets.pets.length) {
      body.append(h('div.card.center', null,
        h('h3', null, 'No eggs yet'),
        h('p.muted', null, 'Eggs appear when you earn your first charm in a subject, and at 10, 25 and 50 stars. Play in the Academy and check back.'),
      ));
    }
    for (const e of s.pets.eggs) {
      const t = PET_TYPES[e.type];
      const ready = e.roundsDone >= e.roundsNeeded;
      const nameInput = h('input', { type: 'text', placeholder: `Name your ${t.name} pet`, maxLength: 14 }) as HTMLInputElement;
      body.append(h('div.card', null,
        h('div.row', null,
          canvasOf((c, w, hh) => drawEgg(c, e, w / 2, hh / 2, 34)),
          h('div', { style: { flex: '1' } },
            h('h3', null, `A ${t.name} egg`),
            h('p.muted.small', null, ready ? 'It is wobbling. Give it a name and tap Hatch!' : `Warm it by finishing ${e.roundsNeeded - e.roundsDone} more learning round${e.roundsNeeded - e.roundsDone === 1 ? '' : 's'}.`),
            h('p.muted.small', null, `From: ${e.from}`),
          ),
        ),
        ready ? h('div.stack', { style: { marginTop: '8px' } }, nameInput, h('button.btn.primary.block', {
          onclick: () => { let name: string | null = null; update(x => { const pet = hatch(x, e.id, nameInput.value); name = pet ? pet.name : null; }); if (name) { sound('chime'); toast(`${name} hatched!`); } },
        }, 'Hatch')) : null,
      ));
    }
    for (const p of s.pets.pets) {
      const t = PET_TYPES[p.type];
      const next = p.stage === 1 ? STAGE2_FED : p.stage === 2 ? STAGE3_FED : null;
      const isActive = s.pets.active === p.id;
      body.append(h('div.card', null,
        h('div.row', null,
          canvasOf((c, w, hh) => drawPet(c, p, w / 2, hh * 0.85, 6)),
          h('div', { style: { flex: '1' } },
            h('h3', null, p.name, h('span.muted.small', null, `  ${t.name} · stage ${p.stage}`)),
            h('p.muted.small', null, t.blurb),
            h('p.muted.small', null, next ? `Fed ${p.fed} of ${next} to grow.` : 'Fully grown!'),
          ),
        ),
        h('div.grid2', { style: { marginTop: '8px' } },
          h('button.btn.small' + (s.gems >= FEED_COST && next ? '.primary' : ''), { disabled: s.gems < FEED_COST || !next, onclick: () => { let grew: boolean | null = null; update(x => { const r = feed(x, p.id); grew = r ? r.grew : null; }); if (grew !== null) { sound(grew ? 'chime' : 'pop'); if (grew) toast(`${p.name} grew!`); } } }, `Feed (${FEED_COST} `, h('span.dot.gem', { style: { display: 'inline-block' } }), ')'),
          h('button.btn.small', { disabled: isActive, onclick: () => { update(x => { x.pets.active = p.id; }); toast(`${p.name} is coming with you.`); } }, isActive ? 'With you' : 'Bring along'),
        ),
      ));
    }
    body.append(h('p.center.muted.small', null, 'One day your pets will be able to play in the Pet Arena.'));
  }

  render();
  const un = subscribe(render);
  return () => un();
}
